/**
 * ReportCardNudgePanel.jsx — Feb 2026 mid-month.
 *
 * Admin control for the seasonal report-card nudge (4-way switch).
 * Same shape as HomepageHeroPanel — grid of buttons, one Active, PUT
 * on click, no redeploy. Mounted inside the "Homepage" admin tab so
 * both seasonal toggles live in one place.
 *
 * One switch, TWO audiences: flipping the position shows the free
 * copy on FreeHome AND the Basic copy on the paid dashboard.
 * Standard / Pro / Pro+ never see the banner (surgical targeting is
 * enforced at the FRONTEND mount site via `isBasicUser` +
 * `!isPaidUser`).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PowerOff } from "lucide-react";
import {
  NUDGE_POSITION_ORDER,
  NUDGE_POSITION_LABELS,
  NUDGE_MESSAGES,
  DEFAULT_NUDGE_POSITION,
} from "../reportCardNudge";
import { fetchReportCardNudge, setReportCardNudge } from "../../lib/api";

const ReportCardNudgePanel = () => {
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchReportCardNudge()
      .then((data) => {
        if (cancelled) return;
        const key =
          data && NUDGE_POSITION_ORDER.includes(data.position)
            ? data.position
            : DEFAULT_NUDGE_POSITION;
        setActive(key);
      })
      .catch(() => {
        if (!cancelled) setActive(DEFAULT_NUDGE_POSITION);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activate = async (positionKey) => {
    if (saving || positionKey === active) return;
    setSaving(true);
    try {
      const res = await setReportCardNudge(positionKey);
      const nextKey = res?.position || positionKey;
      setActive(nextKey);
      toast.success(
        `Nudge set to "${NUDGE_POSITION_LABELS[nextKey]}"`,
        {
          description:
            nextKey === "off"
              ? "Free & Basic users see no banner."
              : "Free & Basic users see the matching banner immediately.",
        },
      );
    } catch (err) {
      toast.error("Could not update nudge position", {
        description: err?.response?.data?.detail || err?.message || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  const previewFor = (key) => {
    if (key === "off") return null;
    const payload = NUDGE_MESSAGES[key];
    return payload?.free?.bold || null;
  };

  return (
    <div className="ae-card p-6 md:p-7" data-testid="admin-report-card-nudge-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
          Report-card nudge banner
        </h3>
        {loading ? (
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#4a6984]/60">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </span>
        ) : (
          <span
            data-testid="admin-report-card-nudge-active-label"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[#1c9e7e]"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Live: {NUDGE_POSITION_LABELS[active || DEFAULT_NUDGE_POSITION]}
          </span>
        )}
      </div>

      <p className="text-sm text-[#4a6984]/85 leading-relaxed mb-5">
        Show the seasonal report-card nudge to Free-tier users on their home
        AND to Basic-tier users on their paid dashboard. Same switch, two
        audiences — Standard, Pro, and Pro+ never see the banner. Changes
        take effect immediately; no redeploy. Manual only — flip it back to
        <strong> Off</strong> when the season passes.
      </p>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        data-testid="admin-report-card-nudge-options"
      >
        {NUDGE_POSITION_ORDER.map((key) => {
          const isActive = key === active;
          const isOff = key === "off";
          const preview = previewFor(key);
          return (
            <button
              key={key}
              type="button"
              disabled={saving || isActive}
              onClick={() => activate(key)}
              data-testid={`admin-report-card-nudge-option-${key}`}
              className={[
                "text-left rounded-2xl border-2 p-5 transition-all",
                isActive
                  ? "border-[#1c9e7e] bg-[#96ded1]/15 shadow-md cursor-default"
                  : "border-[#4a6984]/15 bg-white hover:border-[#4a6984]/40 hover:shadow-sm cursor-pointer",
                saving && !isActive ? "opacity-60 cursor-wait" : "",
              ].join(" ")}
            >
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#4a6984]/70 flex items-center gap-1.5">
                  {isOff ? <PowerOff className="h-3 w-3" /> : null}
                  {NUDGE_POSITION_LABELS[key]}
                </p>
                {isActive && (
                  <span
                    data-testid={`admin-report-card-nudge-active-badge-${key}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#1c9e7e] text-white text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </span>
                )}
              </div>
              {isOff ? (
                <p className="font-extrabold text-[#1c2d3d] text-base leading-snug">
                  No banner shown.
                </p>
              ) : (
                <>
                  <p className="font-extrabold text-[#1c2d3d] text-base leading-snug">
                    {preview}
                  </p>
                  <p className="mt-2 text-xs text-[#4a6984]/70 leading-relaxed">
                    Free + Basic audiences get audience-specific copy for this position.
                  </p>
                </>
              )}
              {!isActive && (
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.14em] text-[#4a6984]/70">
                  Click to make live →
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReportCardNudgePanel;
